<template>
    <div
        class="sbs-base-switcher"
        :class="switcherClasses"
        :aria-checked="modelValue"
        :aria-disabled="disabled"
        role="switch">
        <div class="sbs-base-switcher__wrapper">
            <input
                v-bind="inputAttrs"
                v-model="switcherValue"
                :id="switcherId"
                class="sbs-base-switcher__input visually-hidden"
                type="checkbox"
                :disabled="disabled"
                @focus="inputFocusHandler"
                @blur="inputBlurHandler"
                @click="inputClickHandler">
            <label
                class="sbs-base-switcher__label"
                :class="switcherLabelClass"
                :for="switcherId">
                <!-- @slot Слот по-умолчанию для вставки контента перед Свитчером (дублирует props `label`) -->
                <slot>{{ label }}</slot>

                <div class="sbs-base-switcher__button group trs">
                    <span class="sbs-base-switcher__button-track trs"></span>
                    <span
                        class="sbs-base-switcher__button-bg trs"
                        :class="buttonClasses"
                        :style="buttonStylesNames"></span>
                </div>

                <!-- @slot Слот для вставки контента после Свитчера (дублирует props `append`) -->
                <slot name="append">
                    {{ append }}
                </slot>
            </label>
        </div>

        <!-- @slot Слот для вывода ошибок -->
        <slot
            name="error"
            v-bind="{
                rules,
                valid,
                value: modelValue,
            }">
            <span
                v-if="invalid"
                class="sbs-base-switcher__error-message">
                <SbsIcon
                    v-bind="errorIconData"
                    class="sbs-base-switcher__error-icon" />
                {{ errorMessage || defaultErrorMessage }}
            </span>
        </slot>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import validationMixin from '../../mixins/validation.js';
import { AllColors } from '../../utils/colors.js';
import { getRandomId } from '../../utils/helpers.js';
import { AllSizes } from '../../utils/sizes.js';
import { AllIconGroups } from '../icon/utils/consts.js';

/**
 * Базовый компонент свитчера, используется для переключения между двумя противоположными состояниями.
 */
export default {
    name: 'SbsBaseSwitcher',
    emits: ['update:modelValue', 'change', 'valid', 'invalid'],
    mixins: [themesMixin, validationMixin],
    data() {
        return {
            isFocused: false,
        };
    },
    computed: {
        switcherValue: {
            get() {
                return this.modelValue;
            },
            async set(value) {
                this.dirty = true;

                /**
                 * Событие `update:modelValue` срабатывает при изменении значения.
                 * В качестве параметра - значение после изменения
                 */
                this.$emit('update:modelValue', value);

                /**
                 * Событие `change` срабатывает при изменении значения.
                 * В качестве параметра - значение после изменения
                 */
                this.$emit('change', value);

                if (!this.noValidate) {
                    await this.validate(value);
                }
            },
        },
        switcherClasses() {
            return [
                this.$attrs.class,
                this.colorThemeClass,
                this.colorHoverClass,
                this.shadowThemeClass,
                this.shadowHoverClass,
                this.borderThemeClass,
                this.borderHoverClass,
                [`sbs-base-switcher--${this.size}`],
                {
                    'focused': this.isFocused,
                    'active': this.modelValue,
                    'disabled': this.disabled,
                    'bordered': this.bordered,
                    'full-width': this.fullWidth,
                },
            ];
        },
        buttonClasses() {
            const classes = [];

            if (this.backgroundHoverClass) {
                classes.push(`group-hover-bg-${this.hoverBackground}`);
            }

            return [
                ...classes,
                this.backgroundThemeClass,
            ];
        },
        buttonStylesNames() {
            return {
                backgroundColor: this.buttonBackgroundColor,
            };
        },
        buttonBackgroundColor() {
            let backgroundColor = '';

            if (!this.backgroundTheme && this.backgroundColor) {
                backgroundColor = this.backgroundColor;
            }

            return backgroundColor;
        },
        switcherId() {
            return this.id || `sbs-base-switcher-${getRandomId()}`;
        },
        errorIconData() {
            return {
                iconName: 'exclamation',
                iconGroup: AllIconGroups.SOLID,
                iconSize: AllSizes.SMALL,
                colorTheme: AllColors.WHITE,
                backgroundTheme: AllColors.GRADIENT_PINK,
            };
        },
        inputAttrs() {
            const { class: _, ...attrs } = this.$attrs;

            return attrs;
        },
    },
    methods: {
        inputFocusHandler() {
            this.isFocused = true;
        },
        inputBlurHandler() {
            this.isFocused = false;
        },
        inputClickHandler(e) {
            e.target.blur();
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Контент, расположенный после свитчера
         */
        append: {
            type: String,
            default: '',
        },
        /**
         * Позволяет задать кастомный цвет фона свитчера
         */
        backgroundColor: {
            type: String,
            default: '',
        },
        /**
         * Перевод в неактивное состояние
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Сообщение при ошибке
         */
        errorMessage: {
            type: String,
            default: '',
        },
        /**
         * Определяет будет ли свитчер занимать всю доступную ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Контент, расположенный до свитчера
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Класс для лейбла свитчера
         */
        switcherLabelClass: {
            type: [String, Array],
            default: '',
        },
        /**
         * Размер свитчера
         */
        size: {
            type: String,
            default: 'large',
        },
        /**
         * Состояние свитчера (включен или нет)
         * @model
         */
        modelValue: {
            type: Boolean,
            required: true,
        },
    },
};
</script>

<style lang="scss">
.sbs-base-switcher {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    max-width: 100%;

    &--compact {
        .sbs-base-switcher__button {
            width: 40px;
            height: 25px;

            &::after {
                width: 17px;
                height: 17px;
            }
        }
    }

    &--large {
        .sbs-base-switcher__button {
            width: 50px;
            height: 30px;

            &::after {
                width: 22px;
                height: 22px;
            }
        }
    }

    &.full-width {
        width: 100%;
    }

    &__wrapper {
        .sbs-base-switcher.disabled & {
            cursor: not-allowed;
            opacity: 0.5;
            user-select: none;

            & > * {
                pointer-events: none;
            }
        }
    }

    &__label {
        display: flex;
        gap: 10px;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;

        @include typo-secondary-book;
    }

    &__button {
        position: relative;
        z-index: 2;
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
        align-items: center;
        border-radius: $rounded-pill;
        outline-offset: 3px;
        user-select: none;

        .sbs-base-switcher.focused & {
            outline: 1px auto $current-color;
        }

        .sbs-base-switcher.full-width & {
            margin-right: 0;
            margin-left: auto;
        }

        &::after {
            position: absolute;
            top: 4px;
            left: 4px;
            z-index: 3;
            flex-grow: 0;
            flex-shrink: 0;
            width: 22px;
            height: 22px;
            background-color: $white;
            border-radius: $rounded-circle;
            transform: translateX(0);
            transition: inherit;
            content: "";

            .sbs-base-switcher.active & {
                transform: translateX(93%);
            }

            .sbs-base-switcher.disabled:not(.active) & {
                background-color: $gray-deep;
                opacity: 0.4;
            }
        }

        &:hover {
            opacity: 0.5;
        }
    }

    &__button-track,
    &__button-bg {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 2;
        border-radius: inherit;
    }

    &__button-track {
        background-color: $gray-light;

        .sbs-base-switcher.active & {
            opacity: 0;
        }
    }

    &__button-bg {
        .sbs-base-switcher:not(.active) & {
            opacity: 0;
        }
    }

    &__error-message {
        margin-top: 8px;
        color: $danger;

        @include typo-subsecondary-demi;
    }

    &__error-icon {
        width: 15px;
        height: 15px;
        margin-right: 5px;
        border-radius: $rounded-circle;
    }
}
</style>
